import React from "react";
import { useQuery } from "@apollo/client";
import { formatDateFull } from "~/src/utils/formatting";
import { errorModal } from "~/src/utils/errors";
import useAuth from "~/src/auth/hooks/use-auth";
import useModalState from "~/src/hooks/useModalState";
import { RotaButtonNew } from "@teamrota/rota-design";
import { Column } from "~/src/components/grid";
import FETCH_MEMBER_TIMESHEETS from "~/src/graphql/queries/get-member-timesheets/get-member-timesheets.query";
import FETCH_MEMBER_TIMESHEETS_COUNT from "~/src/graphql/queries/get-member-timesheets/get-member-timesheets-count.query";
import ViewEditTimesheet from "~/src/containers/modals/view-edit-timesheet";
import { formatTimesheetDuration } from "~/src/utils/timesheetTransformations";
import {
  EmptyBookings,
  StyledButton,
  Text,
  Row,
  LoadMoreWrapper
} from "./member-shift-list.styles";

const TimesheetsTab = ({
  memberId,
  memberQueryData,
  itemsCount,
  onTimesheetCountUpdate,
  onOpenBookingModal,
  onOpenShiftModal
}) => {
  const auth = useAuth();
  const {
    selectedTimesheetMemberRowId,
    selectedTimesheetMemberRowData,
    selectedTimesheetStatus,
    sourceAccountId,
    handleOpenTimesheetModal,
    handleCloseTimesheetModal
  } = useModalState();

  // Timesheet query
  const {
    data: timesheetData,
    loading: timesheetLoading,
    fetchMore: fetchMoreTimesheets
  } = useQuery(FETCH_MEMBER_TIMESHEETS, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: auth.addVals(FETCH_MEMBER_TIMESHEETS, {
      limit: 10,
      offset: 0,
      memberId: memberId,
      startBefore: memberQueryData.startBefore || undefined,
      startAfter: memberQueryData.startAfter || undefined,
      orderByColumn: "scheduledStartTime",
      orderByDirection: "desc"
    })
  });

  // Fetch timesheet count separately (always runs)
  const { data: timesheetCountData } = useQuery(FETCH_MEMBER_TIMESHEETS_COUNT, {
    fetchPolicy: "cache-first",
    variables: auth.addVals(FETCH_MEMBER_TIMESHEETS_COUNT, {
      memberId: memberId
    }),
    onCompleted: data => {
      // Notify parent component of timesheet count
      const count = data?.memberTimesheetsCount?.totalCount || 0;
      if (onTimesheetCountUpdate) {
        onTimesheetCountUpdate(count);
      }
    }
  });

  const timesheets = timesheetData?.memberTimesheets?.data || [];
  const timesheetCount =
    timesheetCountData?.memberTimesheetsCount?.totalCount || 0;

  // Debug logging for timesheets
  console.log("Timesheet Debug:", {
    timesheetData,
    timesheetCountData,
    timesheetCount,
    itemsCount,
    loading: timesheetLoading
  });

  const loadMore = async () => {
    try {
      await fetchMoreTimesheets({
        variables: {
          offset: timesheets?.length || 10
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            ...prev,
            memberTimesheets: {
              ...prev.memberTimesheets,
              data: [
                ...prev.memberTimesheets.data,
                ...fetchMoreResult.memberTimesheets.data
              ]
            }
          };
        }
      });
    } catch (e) {
      errorModal(e);
    }
  };

  const shouldDisableLoadMore = () => {
    if (!timesheets || !timesheets.length) {
      return true;
    }

    // For timesheets, check against totalResults
    const totalResults = timesheetData?.memberTimesheets?.totalResults;
    if (totalResults && totalResults <= timesheets.length) {
      return true;
    }

    return false;
  };

  return (
    <>
      <ViewEditTimesheet
        isOpen={
          !!selectedTimesheetMemberRowId || !!selectedTimesheetMemberRowData
        }
        onClose={handleCloseTimesheetModal}
        id={selectedTimesheetMemberRowId}
        timesheetMemberRowData={selectedTimesheetMemberRowData}
        sourceAccountId={sourceAccountId}
        selectedTimesheetStatus={selectedTimesheetStatus}
      />

      {timesheets?.length ? (
        timesheets.map((timesheet, index) => {
          const { hoursDisplay, breakText } = formatTimesheetDuration(
            timesheet.totalMinutes,
            timesheet.breakMinutes
          );

          // Format times for display
          const checkInDisplay = timesheet.checkInTime
            ? formatDateFull(timesheet.checkInTime)
            : formatDateFull(timesheet.scheduledStartTime);
          const checkOutDisplay = timesheet.checkOutTime
            ? formatDateFull(timesheet.checkOutTime)
            : formatDateFull(timesheet.scheduledEndTime);

          return (
            <Row key={index}>
              <Column align="middle" gutter="none" small={8 / 12}>
                <Text>
                  {timesheet.accountName}{" "}
                  {formatDateFull(timesheet.scheduledStartTime)} -{" "}
                  {formatDateFull(timesheet.scheduledEndTime)}
                </Text>
                <Text isSmall>
                  {timesheet.venueName || "Venue not specified"}
                </Text>
                <Text isSmall>{timesheet.roleName || "No role specified"}</Text>
              </Column>
              <Column
                align="middle"
                textAlign="right"
                gutter="none"
                small={4 / 12}
              >
                <div>
                  <Text isSmall>Check-In: {checkInDisplay}</Text>
                  <Text isSmall>Check-Out: {checkOutDisplay}</Text>
                  <Text isSmall>
                    {hoursDisplay}
                    {breakText}
                  </Text>
                </div>

                {/* Action buttons */}
                <StyledButton
                  isSmall
                  isOutline
                  onClick={() => {
                    handleOpenTimesheetModal(
                      timesheet.id,
                      timesheet.sourceAccountId,
                      timesheet.status
                    );
                  }}
                >
                  View Timesheet
                </StyledButton>
                {timesheet.bookingId && (
                  <StyledButton
                    isSmall
                    isOutline
                    onClick={() =>
                      onOpenBookingModal &&
                      onOpenBookingModal(
                        timesheet.bookingId,
                        timesheet.accountName
                      )
                    }
                  >
                    View Booking
                  </StyledButton>
                )}
                {timesheet.shiftId && (
                  <StyledButton
                    isSmall
                    isOutline
                    onClick={() =>
                      onOpenShiftModal && onOpenShiftModal(timesheet.shiftId)
                    }
                  >
                    View Shift
                  </StyledButton>
                )}
              </Column>
            </Row>
          );
        })
      ) : (
        <EmptyBookings>No timesheets</EmptyBookings>
      )}

      {!shouldDisableLoadMore() && (
        <LoadMoreWrapper>
          <RotaButtonNew onClick={loadMore}>Load more</RotaButtonNew>
        </LoadMoreWrapper>
      )}
    </>
  );
};

export default TimesheetsTab;
